import { useEffect, useRef, useState } from "react";
import { isPrerender } from "@/lib/prerender";

/**
 * A muted ambient loop that only plays while it is on screen.
 *
 * The still the clip was generated from sits underneath as a lazy <img>, and
 * the video fades in over it once it is actually playing. Nothing is fetched
 * until the frame comes near the viewport: the image is `loading="lazy"` (a
 * `poster` attribute would be fetched eagerly, even in hidden layouts), and the
 * video has no `src` until it is first needed. Visitors who prefer reduced
 * motion get the still alone.
 */
type LoopVideoProps = {
  src: string;
  poster: string;
  /** Hold the clip still, e.g. while another clip in a set is the active one. */
  paused?: boolean;
  /** Must position the wrapper: `relative` with a size, or `absolute inset-0`. */
  className?: string;
  /** Classes for the <img> and <video> layers (object-fit and so on). */
  mediaClassName?: string;
  /**
   * For a clip at the top of a page: the still loads eagerly at high priority
   * (it is the page's largest paint), and the video waits for the visitor's
   * first scroll, touch or keypress (or 2.5 s on a desktop) so it never
   * competes with the first render.
   */
  priority?: boolean;
};

const LoopVideo = ({ src, poster, paused = false, className = "", mediaClassName = "object-cover", priority = false }: LoopVideoProps) => {
  const wrap = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLVideoElement>(null);
  const visible = useRef(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = ref.current;
    const el = wrap.current;
    if (!video || !el || isPrerender()) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let allowed = !priority;
    const sync = () => {
      if (visible.current && !paused && allowed) {
        if (!video.getAttribute("src")) video.src = src;
        video.play().catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        visible.current = entry.isIntersecting;
        sync();
      },
      { rootMargin: "150px 0px" }
    );
    io.observe(el);

    const events = ["scroll", "pointermove", "touchstart", "keydown"] as const;
    const allow = () => {
      allowed = true;
      events.forEach((e) => window.removeEventListener(e, allow));
      sync();
    };
    let timer = 0;
    if (!allowed) {
      events.forEach((e) => window.addEventListener(e, allow, { passive: true, once: true }));
      if (!window.matchMedia("(pointer: coarse)").matches) timer = window.setTimeout(allow, 2500);
    }
    sync();
    return () => {
      io.disconnect();
      window.clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, allow));
    };
  }, [paused, src, priority]);

  return (
    <div ref={wrap} className={`overflow-hidden ${className}`}>
      <img
        src={poster}
        alt=""
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        width={1280}
        height={716}
        className={`absolute inset-0 h-full w-full ${mediaClassName}`}
      />
      <video
        ref={ref}
        muted
        loop
        playsInline
        preload="none"
        aria-hidden="true"
        tabIndex={-1}
        disablePictureInPicture
        onPlaying={() => setPlaying(true)}
        className={`absolute inset-0 h-full w-full transition-opacity duration-500 ${mediaClassName} ${
          playing ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
};

export default LoopVideo;
